import { FC, useState, useEffect } from 'react';
import Spacer from '@components/Spacer';
import * as ApiModels from '@typings/api-models';
import ListCell from '@components/DesignLibrary/ListCell';
import ArrowLeft from '@components/Icons/ArrowLeft';
import Button from '@components/DesignLibrary/Button/Button';
import s from './AdditionalTermsInfo.module.scss';

type PersonBasic = Pick<ApiModels.Person, 'id' | 'name' | 'email'> & {
  color?: string | null;
  logo?: string | null;
  source?: string | null;
};

type Props = {
  onUserSelect: (user: PersonBasic) => void;
  selectedUser: PersonBasic | null;
  collaborators: PersonBasic[];
  onBack: () => void;
  label: string;
  excludedUserIds?: string[];
};

const AssignUserToSignature: FC<Props> = ({
  onUserSelect,
  selectedUser,
  collaborators,
  onBack,
  label,
  excludedUserIds = [],
}) => {
  const [localSelected, setLocalSelected] = useState<PersonBasic | null>(selectedUser);
  const filteredCollaborators = collaborators.filter(c => !excludedUserIds.includes(c.id));

  const teamMembers = filteredCollaborators.filter(user => user.source === "creator-team-member");
  const externalCollaborators = filteredCollaborators.filter(user => user.source !== "creator-team-member");

  // Sync localSelected with selectedUser prop
  useEffect(() => {
    if (selectedUser) {
      setLocalSelected(selectedUser);
    }
  }, [selectedUser]);

  const handleUserSelect = (user: PersonBasic): void => {
    setLocalSelected(user);
  };

  const handleNext = (): void => {
    if (localSelected) {
      onUserSelect(localSelected);
    }
  };

  return (
    <div className={s.container}>
      <button className={s.backButton} onClick={onBack} aria-label="Back">
        <ArrowLeft size={24} />
      </button>
      <p className='body-sm-bold' style={{ textAlign: 'center' }}>{label}</p>
      <Spacer size={20} />
      <div className={s.userListWrapper}>
        <div className={s.userList}>
          {/* External Collaborators Section */}
          <h3 className={s.sectionHeading}>External Collaborators</h3>
          {externalCollaborators.map((user) => (
            <ListCell
              key={user.id}
              label={user.name || ''}
              bottomLine={user.email || ''}
              showCheckbox
              avatar={{
                color: user.color ?? '',
                name: user.name || '',
                url: user.logo || '',
              }}
              isSelected={localSelected?.id === user.id}
              onClick={() => handleUserSelect(user)}
            />
          ))}

          {/* Team Members Section */}
          <h3 className={s.sectionHeading}>Team Members</h3>
          {teamMembers.map((user) => (
            <ListCell
              key={user.id}
              label={user.name || ''}
              bottomLine={user.email || ''}
              showCheckbox
              avatar={{
                color: user.color ?? '',
                name: user.name || '',
                url: user.logo || '',
              }}
              isSelected={localSelected?.id === user.id}
              onClick={() => handleUserSelect(user)}
            />
          ))}
        </div>
      </div>
      <Spacer size={20} />
      <Button
        variant="primary"
        size="md"
        fullWidth
        disabled={!localSelected}
        onClick={handleNext}
      >
        Next
      </Button>
    </div>
  );
};

export default AssignUserToSignature;
